import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Select from 'react-select';

// Interface for the nested client data within an Order
interface OrderClient {
  id: number;
  name: string; // Corrected: Use name as per Client model
}

interface Order {
  id: number;
  orderNumber: string;
  clientId: number;
  status: string;
  finalAmount: number;
  paidAmount: number;
  Client?: OrderClient; // Use the defined interface
}

// Interface for the paginated response from /orders endpoint
interface PaginatedOrdersResponse {
  orders: Order[];
  totalPages: number;
  currentPage: number;
  totalCount: number;
}

interface OrderOption {
  value: number;
  label: string;
  orderData: Order;
}

interface PaymentFormData {
  orderId: number | null;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  reference?: string;
  notes?: string;
}

const PAYMENT_METHODS = [
  'Банковский перевод',
  'Наличные',
  'Карта (POS)',
  'Онлайн-оплата',
  'Kaspi Pay',
  'Другое'
];

const PaymentForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { can } = useAuth(); // Changed from isAllowed

  const isEditMode = !!id;

  const queryParams = new URLSearchParams(location.search);
  const orderIdFromQuery = queryParams.get('orderId');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [orderOptions, setOrderOptions] = useState<OrderOption[]>([]);
  const [selectedOrderOption, setSelectedOrderOption] = useState<OrderOption | null>(null);

  const { register, handleSubmit, setValue, watch, control, formState: { errors } } = useForm<PaymentFormData>({
    defaultValues: {
      orderId: orderIdFromQuery ? Number(orderIdFromQuery) : null,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'Банковский перевод',
      amount: 0,
      reference: '',
      notes: ''
    }
  });

  const watchOrderId = watch('orderId');

  useEffect(() => {
    if (watchOrderId && orderOptions.length > 0) {
      const option = orderOptions.find(o => o.value === watchOrderId);
      setSelectedOrderOption(option || null);
    } else {
      setSelectedOrderOption(null);
    }
  }, [watchOrderId, orderOptions]);

  useEffect(() => {
    const loadFormData = async () => {
      setLoading(true);
      setError(null);

      try {
        // --- Expect PaginatedOrdersResponse from /orders --- 
        const ordersResponse = await axios.get<PaginatedOrdersResponse>('/orders', {
          params: {
            // Fetch all orders suitable for payment dropdown (potentially large list)
            limit: 1000,
            // Optional: Add specific statuses if needed, e.g.
            status: 'reserved,waitingPayment,partiallyPaid' // Or handle filtering client-side
          }
        });

        // --- Extract orders array from response.data.orders --- 
        const allFetchedOrders = ordersResponse.data.orders;

        // --- Check if allFetchedOrders is an array before filtering ---
        if (!Array.isArray(allFetchedOrders)) {
          console.error('Invalid data received for orders:', ordersResponse.data);
          throw new Error('Не удалось загрузить список заказов: неверный формат данных.');
        }

        const fetchedOrders = allFetchedOrders.filter(o =>
          // Filter client-side for simplicity, consider server-side filtering for performance
          o.status !== 'completed' && o.status !== 'cancelled' && o.status !== 'fullyPaid'
        );

        const orderOpts = fetchedOrders.map(order => ({
          value: order.id,
          // --- Use Client.name instead of Client.companyName --- 
          label: `#${order.orderNumber || order.id} - ${order.Client?.name || '?'} (${formatCurrency(order.finalAmount - order.paidAmount)} KZT)`,
          orderData: order
        }));
        setOrderOptions(orderOpts);

        if (isEditMode) {
          const paymentResponse = await axios.get(`/payments/${id}`);
          const paymentData = paymentResponse.data;

          setValue('orderId', paymentData.orderId);
          setValue('amount', paymentData.amount);
          setValue('paymentDate', paymentData.paymentDate?.split('T')[0] || new Date().toISOString().split('T')[0]);
          setValue('paymentMethod', paymentData.paymentMethod || 'Банковский перевод');
          setValue('reference', paymentData.reference || '');
          setValue('notes', paymentData.notes || '');

          let selectedOpt = orderOpts.find(opt => opt.value === paymentData.orderId);

          if (!selectedOpt) {
            try {
              console.log(`Order ${paymentData.orderId} not in initial list, fetching details...`);
              const specificOrderRes = await axios.get<Order>(`/orders/${paymentData.orderId}`);
              const specificOrder = specificOrderRes.data;
              selectedOpt = {
                value: specificOrder.id,
                label: `#${specificOrder.orderNumber || specificOrder.id} - ${specificOrder.Client?.name || '?'} (${specificOrder.status === 'fullyPaid' || specificOrder.status === 'completed' ? 'Оплачен/Завершен' : 'Заказ'})`,
                orderData: specificOrder
              };
              setOrderOptions(prev => [selectedOpt!, ...prev.filter(o => o.value !== specificOrder.id)]);
            } catch (orderErr) {
              console.error('Error fetching specific order for payment:', orderErr);
              selectedOpt = {
                value: paymentData.orderId,
                label: `Заказ #${paymentData.orderId} (Детали недоступны)`,
                orderData: {
                  id: paymentData.orderId,
                  orderNumber: String(paymentData.orderId),
                  clientId: 0,
                  finalAmount: 0,
                  paidAmount: 0,
                  status: 'unknown'
                }
              };
              setOrderOptions(prev => [selectedOpt!, ...prev.filter(o => o.value !== paymentData.orderId)]);
            }
          }
          if (selectedOpt) {
            setSelectedOrderOption(selectedOpt);
          }

        } else if (orderIdFromQuery) {
          const initialOpt = orderOpts.find(opt => opt.value === Number(orderIdFromQuery));
          if (initialOpt) {
            setSelectedOrderOption(initialOpt);
            setValue('orderId', initialOpt.value);
            const initialRemaining = initialOpt.orderData.finalAmount - initialOpt.orderData.paidAmount;
            if (initialRemaining > 0) {
              setValue('amount', initialRemaining);
            }
          }
        }

      } catch (err: any) {
        console.error('Error loading form data:', err);
        setError(err.message || 'Ошибка при загрузке данных формы');
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
  }, [id, isEditMode, setValue, orderIdFromQuery]);

  const onSubmit = async (data: PaymentFormData) => {
    if (!data.amount || data.amount <= 0) {
      setError('Сумма платежа должна быть положительной.');
      return;
    }
    if (!data.orderId) {
      setError('Необходимо выбрать заказ.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (isEditMode) {
        await axios.put(`/payments/${id}`, data);
      } else {
        await axios.post('/payments', data);
      }

      navigate(selectedOrderOption ? `/orders/${selectedOrderOption.value}` : '/payments');
    } catch (err: any) {
      console.error('Error saving payment:', err);
      setError(err.response?.data?.message || 'Ошибка при сохранении платежа');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number | undefined | null): string => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Use the existing isEditMode defined on line 64
  if (isEditMode ? !can('payments.edit') : !can('payments.create')) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-red-600">У вас нет прав доступа к этой странице</p>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  const remainingAmount = selectedOrderOption ?
    selectedOrderOption.orderData.finalAmount - selectedOrderOption.orderData.paidAmount : null;

  return (
    <div>
      <PageHeader
        title={isEditMode ? 'Редактировать Платеж' : 'Добавить Платеж'}
        backLink={selectedOrderOption ? `/orders/${selectedOrderOption.value}` : '/payments'}
      />

      <div className="bg-white rounded-lg shadow p-6 max-w-4xl mx-auto">
        {error && (
          <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label htmlFor="orderId" className="form-label">Заказ *</label>
            <Controller
              name="orderId"
              control={control}
              rules={{ required: 'Необходимо выбрать заказ' }}
              render={({ field, fieldState }) => (
                <Select<OrderOption>
                  inputId='orderId'
                  options={orderOptions}
                  value={selectedOrderOption}
                  onChange={(option: any) => {
                    field.onChange(option ? option.value : null);
                    setSelectedOrderOption(option);
                  }}
                  placeholder="Выберите заказ..."
                  noOptionsMessage={() => 'Нет доступных заказов для оплаты'}
                  isDisabled={isEditMode}
                  isLoading={loading && orderOptions.length === 0}
                  classNamePrefix="react-select"
                  styles={{
                    control: (base: any, state: any) => ({
                      ...base,
                      borderColor: fieldState.error ? '#ef4444' : base.borderColor,
                      backgroundColor: isEditMode ? '#f3f4f6' : base.backgroundColor
                    }),
                    menu: (base: any) => ({ ...base, zIndex: 20 })
                  }}
                />
              )}
            />
            {errors.orderId && (
              <p className="mt-1 text-sm text-red-600">{errors.orderId.message}</p>
            )}
          </div>

          {selectedOrderOption && (
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
              <h3 className="font-medium text-base mb-2 text-gray-700">Информация по заказу #{selectedOrderOption.orderData.orderNumber || selectedOrderOption.orderData.id}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
                <div>
                  <span className="text-gray-500">Клиент:</span>
                  <p className="font-medium text-gray-800">
                    {selectedOrderOption.orderData.Client?.name || '-'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Итого к оплате:</span>
                  <p className="font-medium text-gray-800">{formatCurrency(selectedOrderOption.orderData.finalAmount)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Осталось оплатить:</span>
                  <p className={`font-medium ${remainingAmount !== null && remainingAmount > 0.005 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(remainingAmount !== null && remainingAmount < 0.005 ? 0 : remainingAmount)}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-200">
            <div>
              <label htmlFor="amount" className="form-label">Сумма платежа *</label>
              <div className="relative">
                <input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  className={`form-input pr-16 ${errors.amount ? 'border-red-500' : ''}`}
                  placeholder="0.00"
                  {...register('amount', {
                    required: 'Сумма обязательна',
                    valueAsNumber: true,
                    min: { value: 0.01, message: 'Сумма должна быть > 0' }
                  })}
                />
                <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-gray-500">KZT</span>
              </div>
              {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>}
              {remainingAmount !== null && (
                <p className="mt-1 text-xs text-gray-500">Остаток по заказу: {formatCurrency(remainingAmount < 0.005 ? 0 : remainingAmount)}</p>
              )}
            </div>

            <div>
              <label htmlFor="paymentDate" className="form-label">Дата платежа *</label>
              <input
                id="paymentDate"
                type="date"
                className={`form-input ${errors.paymentDate ? 'border-red-500' : ''}`}
                {...register('paymentDate', { required: 'Дата платежа обязательна' })}
                max={new Date().toISOString().split("T")[0]}
              />
              {errors.paymentDate && <p className="mt-1 text-sm text-red-600">{errors.paymentDate.message}</p>}
            </div>

            <div>
              <label htmlFor="paymentMethod" className="form-label">Метод оплаты *</label>
              <select
                id="paymentMethod"
                className={`form-input ${errors.paymentMethod ? 'border-red-500' : ''}`}
                {...register('paymentMethod', { required: 'Метод оплаты обязателен' })}
              >
                {PAYMENT_METHODS.map(method => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
              {errors.paymentMethod && <p className="mt-1 text-sm text-red-600">{errors.paymentMethod.message}</p>}
            </div>

            <div>
              <label htmlFor="reference" className="form-label">Номер счета / Референс</label>
              <input
                id="reference"
                type="text"
                className="form-input"
                placeholder="Например, номер счета или транзакции"
                {...register('reference')}
              />
            </div>
          </div>

          <div>
            <label htmlFor="notes" className="form-label">Заметки</label>
            <textarea
              id="notes"
              rows={3}
              className="form-input"
              placeholder="Дополнительная информация о платеже"
              {...register('notes')}
            />
          </div>

          <div className="pt-5 flex justify-end space-x-3 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(selectedOrderOption ? `/orders/${selectedOrderOption.value}` : '/payments')}
              className="btn-secondary"
              disabled={submitting}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || !selectedOrderOption}
              title={!selectedOrderOption ? "Сначала выберите заказ" : ""}
            >
              {submitting ? <LoadingSpinner /> : (isEditMode ? 'Сохранить Изменения' : 'Добавить Платеж')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentForm;
